var _class, _descriptor, _descriptor2, _descriptor3;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import { assert } from '@ember/debug';
import { dependentKeyCompat } from '@ember/object/compat';
import { ValidationChangeset, getKeyValues } from 'validated-changeset';
import ArrayProxy from '@ember/array/proxy';
import ObjectProxy from '@ember/object/proxy';
import { notifyPropertyChange } from '@ember/object';
import mergeDeep from './utils/merge-deep';
import isObject from './utils/is-object';
import { tracked } from '@glimmer/tracking';
import { get as safeGet, set as safeSet } from '@ember/object';
import { macroCondition, dependencySatisfies, importSync } from '@embroider/macros';
const CHANGES = '_changes';
const PREVIOUS_CONTENT = '_previousContent';
const CONTENT = '_content';
export function buildOldValues(content, changes, getDeep) {
  const obj = Object.create(null);
  for (let change of changes) {
    obj[change.key] = getDeep(content, change.key);
  }
  return obj;
}
function isProxy(o) {
  return !!(o && (o instanceof ObjectProxy || o instanceof ArrayProxy));
}
function maybeUnwrapProxy(o) {
  return isProxy(o) ? maybeUnwrapProxy(safeGet(o, 'content')) : o;
}
let Model;
if (macroCondition(dependencySatisfies('ember-data', '*'))) {
  Model = importSync('@ember-data/model').default;
}
export let EmberValidationChangeset = (_class = class EmberValidationChangeset extends ValidationChangeset {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "_changes", _descriptor, this);
    _initializerDefineProperty(this, "_errors", _descriptor2, this);
    _initializerDefineProperty(this, "_content", _descriptor3, this);
    _defineProperty(this, "isObject", isObject);
    _defineProperty(this, "maybeUnwrapProxy", maybeUnwrapProxy);
    // DO NOT override setDeep. Ember.set does not work wth empty hash and nested
    // key Ember.set({}, 'user.name', 'foo');
    // override base class
    // DO NOT override setDeep. Ember.set does not work with Ember.set({}, 'user.name', 'foo');
    _defineProperty(this, "getDeep", safeGet);
    _defineProperty(this, "mergeDeep", mergeDeep);
  }
  safeGet(obj, key) {
    if (Model && obj.relationshipFor?.(key)?.meta?.kind == 'belongsTo') {
      return obj.belongsTo(key).value();
    }
    return safeGet(obj, key);
  }
  safeSet(obj, key, value) {
    return safeSet(obj, key, value);
  }

  /**
   * @property isValid
   * @type {Array}
   */
  get isValid() {
    return super.isValid;
  }

  /**
   * @property isInvalid
   * @type {Boolean}
   */
  get isInvalid() {
    return super.isInvalid;
  }

  /**
   * @property isPristine
   * @type {Boolean}
   */
  get isPristine() {
    return super.isPristine;
  }

  /**
   * @property isDirty
   * @type {Boolean}
   */
  get isDirty() {
    return super.isDirty;
  }
  get pendingData() {
    let content = this[CONTENT];
    let changes = this[CHANGES];
    let pendingChanges = this.mergeDeep(Object.create(Object.getPrototypeOf(content)), content, {
      safeGet,
      safeSet
    });
    return this.mergeDeep(pendingChanges, changes, {
      safeGet,
      safeSet
    });
  }

  /**
   * Manually add an error to the changeset. If there is an existing
   * error or change for `key`, it will be overwritten.
   *
   * @method addError
   */
  addError(key, error) {
    super.addError(key, error);
    notifyPropertyChange(this, key);
    // Return passed-in `error`.
    return error;
  }

  /**
   * Manually push multiple errors to the changeset as an array.
   *
   * @method pushErrors
   */
  pushErrors(key, ...newErrors) {
    const {
      value,
      validation
    } = super.pushErrors(key, ...newErrors);
    notifyPropertyChange(this, key);
    return {
      value,
      validation
    };
  }

  /**
   * Sets property or error on the changeset.
   * Returns value or error
   */
  _setProperty({
    key,
    value,
    oldValue
  }) {
    super._setProperty({
      key,
      value,
      oldValue
    });
    notifyPropertyChange(this, key);
  }

  /**
   * Notifies virtual properties set on the changeset of a change.
   * You can specify which keys are notified by passing in an array.
   *
   * @private
   * @param {Array} keys
   * @return {Void}
   */
  _notifyVirtualProperties(keys) {
    keys = super._notifyVirtualProperties(keys);
    (keys || []).forEach(key => notifyPropertyChange(this, key));
    return;
  }

  /**
   * Deletes a key off an object and notifies observers.
   */
  _deleteKey(objName, key = '') {
    const result = super._deleteKey(objName, key);
    notifyPropertyChange(this, key);
    return result;
  }

  /**
   * Executes the changeset if in a valid state.
   *
   * @method execute
   */
  execute() {
    let oldContent;
    if (this.isValid && this.isDirty) {
      let content = this[CONTENT];
      let changes = this[CHANGES];

      // keep old values in case of error and we want to rollback
      oldContent = buildOldValues(content, getKeyValues(changes), this.getDeep);

      // we want mutation on original object
      // @tracked
      this[CONTENT] = this.mergeDeep(content, changes, {
        safeGet,
        safeSet
      });
    }
    this[PREVIOUS_CONTENT] = oldContent;
    return this;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "_changes", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "_errors", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "_content", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "isValid", [dependentKeyCompat], Object.getOwnPropertyDescriptor(_class.prototype, "isValid"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isInvalid", [dependentKeyCompat], Object.getOwnPropertyDescriptor(_class.prototype, "isInvalid"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isPristine", [dependentKeyCompat], Object.getOwnPropertyDescriptor(_class.prototype, "isPristine"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isDirty", [dependentKeyCompat], Object.getOwnPropertyDescriptor(_class.prototype, "isDirty"), _class.prototype), _class);

/**
 * Creates new changesets.
 */
export function changeset(obj) {
  assert('Underlying object for changeset is missing', Boolean(obj));
  assert('Array is not a valid type to pass as the first argument to `changeset`', !Array.isArray(obj));
  const c = new EmberValidationChangeset(obj);
  return c;
}

/**
 * Creates new changesets.
 * @function Changeset
 */
export function Changeset(obj) {
  const c = changeset(obj);
  return new Proxy(c, {
    get(targetBuffer, key /*, receiver*/) {
      const res = targetBuffer.get(key.toString());
      return res;
    },
    set(targetBuffer, key, value /*, receiver*/) {
      targetBuffer.set(key.toString(), value);
      return true;
    }
  });
}